import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { readBodyHealthRecordGuards, withBodyHealthRecordGuards, type BodyHealthRecordGuardInfo } from '@/lib/bodyHealthRecordGuards';
import { BODY_HEALTH_CHANGED_EVENT, isBodyHealthChangeFor } from '@/lib/bodyHealthChanges';
// Account-scoped body history (cm/kg). Manual changes belong to the reviewed executor.
export interface BodyMeasurement extends Partial<BodyHealthRecordGuardInfo> {
  id: string;
  user_id?: string;
  client_action_id?: string | null;
  measured_at: string;
  source: "manual" | "scan";
  weight_kg: number | null;
  body_fat_pct: number | null;
  neck_cm: number | null;
  shoulders_cm: number | null;
  chest_cm: number | null;
  waist_cm: number | null;
  hips_cm: number | null;
  bicep_left_cm: number | null;
  bicep_right_cm: number | null;
  thigh_left_cm: number | null;
  thigh_right_cm: number | null;
  calf_left_cm: number | null;
  calf_right_cm: number | null;
  extra_measurements: Record<string, unknown> | null;
  scan_provider: string | null;
  /** FitXpress measurement uuid — phase-2 Body Progress compares two of these. */
  provider_scan_id: string | null;
  /** Vendor 3D model (.obj on their S3) — phase-2 Future Body / avatar work. */
  model_3d_url: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}
export type BodyMeasurementInput = Partial<Omit<BodyMeasurement, "id" | "user_id" | "client_action_id" | "created_at" | "updated_at" | "extra_measurements">> & {
  measured_at?: string;
  extra_measurements?: Record<string, unknown> | null;
};
export const BODY_HISTORY_PAGE_SIZE = 200;
export interface BodyHistoryCursor {
  measured_at: string;
  id: string;
}
const numericFields = ["weight_kg", "body_fat_pct", "neck_cm", "shoulders_cm", "chest_cm", "waist_cm", "hips_cm", "bicep_left_cm", "bicep_right_cm", "thigh_left_cm", "thigh_right_cm", "calf_left_cm", "calf_right_cm"] as const;
const uuid = (v: unknown): v is string => typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
const timeKey = (v: string) => {
  const match = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(?:Z|[+-]\d{2}:\d{2})$/);
  if (!match || !Number.isFinite(Date.parse(v)))
    throw new Error("Invalid body timestamp");
  const [, year, month, day, hour, minute, second, fraction = ""] = match;
  const first = new Date(`${year}-${month}-01T00:00:00Z`);
  first.setUTCMonth(first.getUTCMonth() + 1, 0);
  if (Number(year) < 1 || Number(day) < 1 || Number(day) > first.getUTCDate() || Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59)
    throw new Error("Invalid body timestamp");
  // Date truncates microseconds; keep all six fractional digits for keyset validation.
  return new Date(v).toISOString().slice(0, 19) + "." + fraction.padEnd(6, "0");
};
const compare = (a: BodyHistoryCursor, b: BodyHistoryCursor) => timeKey(a.measured_at).localeCompare(timeKey(b.measured_at)) || a.id.toLowerCase().localeCompare(b.id.toLowerCase());
function bodyRecord(raw: unknown, ownerId: string): BodyMeasurement {
  if (!raw || typeof raw !== "object" || Array.isArray(raw))
    throw new Error("Missing body measurement");
  const r = raw as Record<string, unknown>;
  if (!uuid(r.id) || r.user_id !== ownerId || typeof r.measured_at !== "string" || !['manual', 'scan'].includes(String(r.source)) || (r.scan_provider !== null && typeof r.scan_provider !== 'string') || numericFields.some(k => r[k] !== null && (typeof r[k] !== "number" || !Number.isFinite(r[k]))))
    throw new Error("Body history not confirmed");
  timeKey(r.measured_at);
  return r as unknown as BodyMeasurement;
}
/** A fixed upper bound and a timestamp+ID cursor keep dense history out of offset races. */
export async function readBodyHistoryPage(ownerId: string, options: {
  upper: string;
  cursor?: BodyHistoryCursor | null;
  signal?: AbortSignal;
  assertCurrent: () => void;
  bearer: string;
  pageSize?: number;
}) {
  const size = options.pageSize ?? BODY_HISTORY_PAGE_SIZE;
  if (!uuid(ownerId) || !Number.isInteger(size) || size < 1 || size > 500)
    throw new Error("Invalid body history request");
  timeKey(options.upper);
  const cursor = options.cursor;
  if (cursor) {
    if (!uuid(cursor.id))
      throw new Error("Invalid body history cursor");
    timeKey(cursor.measured_at);
  }
  options.assertCurrent();
  let query = supabase.from("body_measurements").select("*").eq("user_id", ownerId).lte("measured_at", options.upper).order("measured_at", { ascending: false }).order("id", { ascending: false }).limit(size + 1);
  if (cursor)
    query = query.or("measured_at.lt." + cursor.measured_at + ",and(measured_at.eq." + cursor.measured_at + ",id.lt." + cursor.id + ")");
  if (options.signal)
    query = query.abortSignal(options.signal);
  const { data, error } = await query.setHeader("Authorization", "Bearer " + options.bearer);
  options.assertCurrent();
  if (error || !Array.isArray(data) || data.length > size + 1)
    throw error ?? new Error("Missing body history");
  const rows = data.map(row => bodyRecord(row, ownerId));
  if (rows.some((row, i) => timeKey(row.measured_at) > timeKey(options.upper) || (cursor && compare(row, cursor) >= 0) || (i > 0 && compare(rows[i - 1], row) <= 0)) || new Set(rows.map(row => row.id.toLowerCase())).size !== rows.length)
    throw new Error("Body history changed");
  const observed = rows.slice(0, size), hasMore = rows.length > size;
  const guards = await readBodyHealthRecordGuards(ownerId, 'body', observed.map(row => row.id), options);
  options.assertCurrent();
  return { records: withBodyHealthRecordGuards(observed, guards), hasMore, complete: !hasMore, cursor: hasMore ? { measured_at: observed.at(-1)!.measured_at, id: observed.at(-1)!.id } : null, upper: options.upper };
}
export function useBodyMeasurements() {
  const [measurements, setMeasurements] = useState<BodyMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [identityGeneration, setIdentityGeneration] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const owner = useRef<string | null>(null);
  const epoch = useRef(0);
  const ticket = useRef(0);
  const mounted = useRef(false);
  const controllers = useRef(new Set<AbortController>());
  // Refresh replaces reads; only an identity change also cancels legacy writes.
  const readControllers = useRef(new Set<AbortController>());
  const reading = useRef(false);
  const writing = useRef<symbol | null>(null);
  const page = useRef<{
    cursor: BodyHistoryCursor | null;
    upper: string;
    rows: BodyMeasurement[];
  } | null>(null);
  const retire = useCallback((id: string | null) => {
    epoch.current++;
    ticket.current++;
    controllers.current.forEach(controller => controller.abort());
    controllers.current.clear();
    readControllers.current.clear();
    reading.current = false;
    writing.current = null;
    owner.current = id;
    page.current = null;
    setIdentityGeneration(epoch.current);
    setUserId(id);
    setMeasurements([]);
    setHasData(false);
    setHasMore(false);
    setError(false);
    setLoading(!!id);
    setIsRefreshing(false);
  }, []);
  // Bind the actor to the render, so even a retained callback cannot survive ABA.
  const capture = useCallback(() => {
    const generation = identityGeneration;
    return () => mounted.current && !!userId && owner.current === userId && epoch.current === generation;
  }, [userId, identityGeneration]);
  const auth = async (id: string, assert: () => void) => {
    assert();
    const u = await supabase.auth.getUser();
    assert();
    if (u.error || u.data.user?.id !== id) throw new Error("Body session changed");
    const s = await supabase.auth.getSession();
    assert();
    if (s.error || s.data.session?.user.id !== id || !s.data.session.access_token) throw new Error("Body session changed");
    return s.data.session.access_token;
  };
  const read = useCallback(async (append = false) => {
    if (append && (reading.current || !page.current?.cursor))
      return;
    if (!append) {
      ticket.current++;
      readControllers.current.forEach(c => c.abort());
      readControllers.current.clear();
    }
    const run = ++ticket.current, generation = epoch.current, c = new AbortController();
    controllers.current.add(c);
    readControllers.current.add(c);
    reading.current = true;
    setIsRefreshing(true);
    const current = () => mounted.current && run === ticket.current && generation === epoch.current && !c.signal.aborted;
    const assert = () => { if (!current()) throw new Error("Body session changed"); };
    const timer = window.setTimeout(() => {
      if (current()) {
        setError(true);
        setLoading(false);
        setIsRefreshing(false);
        reading.current = false;
      }
      c.abort();
    }, 12000);
    try {
      const s = await supabase.auth.getSession();
      assert();
      if (s.error)
        throw s.error;
      const id = s.data.session?.user.id ?? null;
      if (!id) {
        retire(null);
        return;
      }
      if (owner.current !== id) {
        retire(id);
        queueMicrotask(() => { if (mounted.current && owner.current === id)
          void read(false); });
        return;
      }
      const bearer = await auth(id, assert), previous = append ? page.current : null, upper = previous?.upper ?? new Date().toISOString();
      const result = await readBodyHistoryPage(id, { upper, cursor: previous?.cursor, signal: c.signal, assertCurrent: assert, bearer });
      assert();
      const rows = previous ? [...previous.rows, ...result.records] : result.records;
      if (new Set(rows.map(row => row.id.toLowerCase())).size !== rows.length)
        throw new Error("Body history changed");
      page.current = { rows, upper, cursor: result.cursor };
      setMeasurements(rows);
      setHasMore(result.hasMore);
      setHasData(true);
      setError(false);
      setLoading(false);
    }
    catch (cause) {
      if (current()) {
        console.error("[useBodyMeasurements] read failed", cause);
        setError(true);
      }
    }
    finally {
      window.clearTimeout(timer);
      controllers.current.delete(c);
      readControllers.current.delete(c);
      if (current()) {
        reading.current = false;
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [retire]);
  const stop = useCallback(() => {
    mounted.current = false;
    epoch.current++;
    controllers.current.forEach(controller => controller.abort());
    controllers.current.clear();
    readControllers.current.clear();
  }, []);
  useEffect(() => {
    mounted.current = true;
    setIdentityGeneration(epoch.current);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (next === owner.current)
        return;
      retire(next);
      if (next)
        queueMicrotask(() => { if (mounted.current && owner.current === next)
          void read(); });
    });
    void read();
    return () => { stop(); subscription.unsubscribe(); };
  }, [read, retire, stop]);
  useEffect(() => {
    const changed = (event: Event) => { if (isBodyHealthChangeFor(event, owner.current, 'body')) void read(false); };
    window.addEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
    return () => window.removeEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
  }, [read]);
  const mutate = useCallback(async <T,>(operation: (context: {
    id: string;
    bearer: string;
    signal: AbortSignal;
    assert: () => void;
  }) => Promise<T>): Promise<T | null> => {
    const actor = capture();
    const assert = () => { if (!actor()) throw new Error("Body session changed"); };
    if (writing.current)
      throw new Error("Body write already in progress");
    const lock = Symbol(), c = new AbortController();
    writing.current = lock;
    controllers.current.add(c);
    try {
      assert();
      const id = userId!, bearer = await auth(id, assert);
      const result = await operation({ id, bearer, signal: c.signal, assert });
      assert();
      await read(false);
      assert();
      return result;
    }
    catch (cause) {
      if (actor())
        console.error("[useBodyMeasurements] write failed", cause);
      return null;
    }
    finally {
      controllers.current.delete(c);
      if (writing.current === lock)
        writing.current = null;
    }
  }, [capture, userId, read]);
  // The existing camera/provider pipeline remains ingestion, separate from manual review.
  const addScan = useCallback((input: BodyMeasurementInput, opts?: {
    clientActionId?: string;
  }) => {
    const draft = structuredClone(input), actionId = opts?.clientActionId;
    if (draft.source !== 'scan' || !['3dlook', 'mock'].includes(String(draft.scan_provider)) || (draft.scan_provider === '3dlook' && !uuid(draft.provider_scan_id))) return Promise.resolve(null);
    return mutate(async ({ id, bearer, signal, assert }) => {
      const { data, error } = await supabase.from("body_measurements").insert({ ...draft, user_id: id, ...(actionId ? { client_action_id: actionId } : {}) } as Database["public"]["Tables"]["body_measurements"]["Insert"]).select().abortSignal(signal).single().setHeader("Authorization", "Bearer " + bearer);
      assert();
      if (error?.code === '23505' && actionId) {
        const existing = await supabase.from("body_measurements").select("*").eq("user_id", id).eq("client_action_id", actionId).abortSignal(signal).maybeSingle().setHeader("Authorization", "Bearer " + bearer);
        assert();
        if (existing.error || !existing.data)
          throw existing.error ?? error;
        return bodyRecord(existing.data, id);
      }
      if (error || !data)
        throw error ?? new Error("Body insert not confirmed");
      const saved = bodyRecord(data, id);
      if (typeof draft.weight_kg === 'number') {
        assert();
        const profile = await supabase.from("user_profiles").update({ current_weight_kg: draft.weight_kg }).eq("user_id", id).abortSignal(signal).setHeader("Authorization", "Bearer " + bearer);
        assert();
        if (profile.error)
          console.warn("[useBodyMeasurements] profile weight sync failed", profile.error);
      }
      return saved;
    });
  }, [mutate]);
  // Retained callbacks from older mounted surfaces fail closed, without outbound writes.
  const add = useCallback(async (_input: BodyMeasurementInput, _opts?: { clientActionId?: string }): Promise<BodyMeasurement | null> => null, []);
  const update = useCallback(async (_id: string, _patch: BodyMeasurementInput): Promise<BodyMeasurement | null> => null, []);
  const remove = useCallback(async (_id: string): Promise<boolean> => false, []);
  return {
    measurements, loading, error, userId, identityGeneration, capture,
    isRefreshing, hasData, hasMore,
    complete: hasData && !hasMore && !error && !isRefreshing,
    status: error ? 'error' : !userId && !loading ? 'signed-out' : loading ? 'loading' : 'ready',
    reload: () => read(false), loadMore: () => read(true), addScan, add, update, remove,
  };
}
